/* Product-page lookup for the compose flows: pulls the page's scraped image
   assets and its copy, and derives the two things the panel wants from that
   copy — a short product digest for the prompts and headline chips.

   Images come from the DB (Holoscanner snapshot); text prefers the stored
   page_processing extraction and only live-scrapes as a fallback. */
import { useState } from "react"
import { toast } from "react-toastify"
import {
  useAdForgeScrapeTextMutation,
  type ScrapedText,
} from "~/ui-rtk/api/adForgeApi"
import {
  useLazyGetAssetsByPageUrlQuery,
  useLazyGetPageTextByPageUrlQuery,
} from "~/ui-rtk/api/assetsApi"
import {
  useTriggerHoloscannerCrawlMutation,
  useTriggerHoloscannerCrawlCheckMutation,
} from "~/ui-rtk/api/jobsApi"
import {
  assetToScrapedImage,
  isAvifImage,
  type ScrapedImage,
} from "../../../types"

interface UrlProduct {
  name: string | null
  description: string | null
}

export default function useProductPage() {
  const [productUrl, setProductUrl] = useState("")
  const [isFetchingUrl, setIsFetchingUrl] = useState(false)
  const [urlImages, setUrlImages] = useState<ScrapedImage[]>([])
  const [urlTexts, setUrlTexts] = useState<ScrapedText[]>([])
  const [urlProduct, setUrlProduct] = useState<UrlProduct | null>(null)

  const [fetchByPageUrl] = useLazyGetAssetsByPageUrlQuery()
  const [fetchPageText] = useLazyGetPageTextByPageUrlQuery()
  const [triggerCrawl] = useTriggerHoloscannerCrawlMutation()
  const [triggerCrawlCheck] = useTriggerHoloscannerCrawlCheckMutation()
  const [scrapeText] = useAdForgeScrapeTextMutation()

  const handleFetchUrl = async () => {
    const trimmed = productUrl.trim()
    if (!trimmed || isFetchingUrl) return
    setIsFetchingUrl(true)
    try {
      // Two DB lookups in parallel: the page's scraped image assets and its
      // stored page_processing text extraction. Either may fail alone.
      const [assetsRes, pageTextRes] = await Promise.allSettled([
        fetchByPageUrl({ url: trimmed }).unwrap(),
        fetchPageText({ url: trimmed }).unwrap(),
      ])

      if (assetsRes.status === "fulfilled") {
        const { snapshot, assets } = assetsRes.value
        if (!snapshot) {
          // Nothing scraped for this page yet — enqueue an on-demand crawl.
          // It runs out-of-band (Airflow sensor ~every 2 min), so images
          // aren't available immediately; the Jobs inbox pings when ready.
          try {
            await triggerCrawl({
              requests: [{ url: trimmed, pageType: "CONTENT_PAGE" }],
            }).unwrap()
            triggerCrawlCheck({ url: trimmed })
              .unwrap()
              .catch(() => undefined)
            toast.info(
              "No snapshot of that page yet — started scraping it. Fetch again in a few minutes for its images.",
            )
          } catch {
            toast.error("Failed to start scraping that page")
          }
        } else {
          const usable = (assets ?? []).filter(
            a => a.status !== "FAILED" && a.syncStatus !== "FAILED",
          )
          const images = usable
            .map(assetToScrapedImage)
            .filter(a => a.type !== "video" && !isAvifImage(a) && !!a.assetId)
          setUrlImages(images)
          if (images.length > 0) {
            toast.success(
              `Found ${images.length} image${images.length === 1 ? "" : "s"} from the product page`,
            )
          } else {
            toast.info("No usable images found for that page")
          }
        }
      } else {
        toast.error("Failed to look up page assets")
      }

      // Text: prefer the stored DB extraction (instant, already cleaned of
      // boilerplate, includes structured product copy); live-scrape only as
      // a fallback when the page has no extraction yet.
      const dbText =
        pageTextRes.status === "fulfilled" ? pageTextRes.value : null
      if (dbText && (dbText.blocks.length > 0 || dbText.product)) {
        setUrlProduct(dbText.product)
        const mapped: ScrapedText[] = [
          ...(dbText.title
            ? [
                {
                  id: "db-title",
                  content: dbText.title,
                  type: "heading" as const,
                  selected: false,
                },
              ]
            : []),
          ...dbText.blocks.map((b, i) => ({
            id: `db-${i}`,
            content: b.text,
            type: (b.type === "heading" ||
            b.type === "paragraph" ||
            b.type === "list_item"
              ? b.type
              : "other") as ScrapedText["type"],
            selected: false,
          })),
        ]
        setUrlTexts(mapped)
      } else {
        try {
          const scraped = await scrapeText({
            campaignType: "product",
            productUrl: trimmed,
            homepageUrl: trimmed,
            includeReviews: false,
          }).unwrap()
          setUrlTexts(scraped.text ?? [])
          setUrlProduct(null)
        } catch {
          // Text is an enhancement — a failed scrape just means no
          // suggestions.
        }
      }
    } finally {
      setIsFetchingUrl(false)
    }
  }

  // Short digest of the page's copy, fed into the compose prompts so the
  // model knows what the product actually is.
  const scrapedProductContext = (() => {
    const bits: string[] = []
    if (urlProduct?.name) bits.push(urlProduct.name)
    if (urlProduct?.description) bits.push(urlProduct.description)
    const headings = urlTexts
      .filter(t => t.type === "heading")
      .slice(0, 3)
      .map(t => t.content.trim())
    const paragraphs = urlTexts
      .filter(t => t.type === "paragraph")
      .slice(0, 2)
      .map(t => t.content.trim())
    return [...bits, ...headings, ...paragraphs]
      .filter(Boolean)
      .join(" — ")
      .slice(0, 500)
  })()

  // Page headings short enough to work as overlay copy → headline chips.
  const headlineSuggestions = (() => {
    const seen = new Set<string>()
    const out: { id: string; content: string }[] = []
    const push = (id: string, raw: string | null | undefined) => {
      const c = (raw ?? "").trim()
      if (!c || c.length > 60 || seen.has(c.toLowerCase())) return
      seen.add(c.toLowerCase())
      out.push({ id, content: c })
    }
    push("product-name", urlProduct?.name)
    for (const t of urlTexts) {
      if (t.type === "heading") push(t.id, t.content)
    }
    return out.slice(0, 4)
  })()

  return {
    productUrl,
    setProductUrl,
    isFetchingUrl,
    urlImages,
    urlTexts,
    urlProduct,
    handleFetchUrl,
    scrapedProductContext,
    headlineSuggestions,
  }
}
